import LgpdNotice from '@/components/feature/LgpdNotice';
import { useEffect, useState } from 'react';
import { depoimentosPalestra } from '../../mocks/depoimentos';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingCTA from '@/components/feature/FloatingCTA';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import AnimatedStatCard from '@/components/feature/AnimatedStatCard';
import PacotesBox from '@/components/feature/PacotesBox';
import NossosFones from '@/components/feature/NossosFones';

declare const gtag: (command: string, action: string, params?: Record<string, unknown>) => void;

// ─── Quote Form ──────────────────────────────────────────────────────────────

function QuoteForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    empresa: '',
    eventType: '',
    participants: '',
    date: '',
    local: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus('idle');

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));

      const res = await fetch('https://ordem-de-servico.stibpalestrasilenciosasp.com.br/pedido.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          empresa: '',
          eventType: '',
          participants: '',
          date: '',
          local: '',
          message: '',
        });
        if (typeof gtag !== 'undefined') {
          gtag('event', 'generate_lead', { event_category: 'Form', event_label: 'SP Quote' });
        }
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setStatus('idle'), 8000);
    }
  };

  const inputClass =
    'w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none font-body text-sm text-gray-800 bg-white transition-colors';
  const labelClass = 'block font-body text-sm font-semibold text-gray-700 mb-1.5';

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      id="sp-quote-form"
      aria-label="Formulário de orçamento palestra silenciosa São Paulo"
      className="space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="sp-name" className={labelClass}>Nome Completo *</label>
          <input
            type="text"
            id="sp-name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Seu nome"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="sp-email" className={labelClass}>E-mail corporativo *</label>
          <input
            type="email"
            id="sp-email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="seu@empresa.com.br"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="sp-phone" className={labelClass}>Telefone / WhatsApp *</label>
          <input
            type="tel"
            id="sp-phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            placeholder="(11) 9 9999-9999"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="sp-empresa" className={labelClass}>Empresa / Organização</label>
          <input
            type="text"
            id="sp-empresa"
            name="empresa"
            value={formData.empresa}
            onChange={handleChange}
            placeholder="Nome da empresa ou entidade"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="sp-eventType" className={labelClass}>Tipo de Evento *</label>
          <select
            id="sp-eventType"
            name="eventType"
            required
            value={formData.eventType}
            onChange={handleChange}
            className={inputClass}
          >
            <option value="">Selecione...</option>
            <option value="Congresso / Conferência">Congresso / Conferência</option>
            <option value="Evento Corporativo">Evento Corporativo</option>
            <option value="Feira e Exposição">Feira e Exposição</option>
            <option value="Summit de Tecnologia">Summit de Tecnologia</option>
            <option value="Evento de Moda / Design">Evento de Moda / Design</option>
            <option value="Treinamento Corporativo">Treinamento Corporativo</option>
            <option value="Evento Multilíngue">Evento Multilíngue</option>
            <option value="Workshop / Capacitação">Workshop / Capacitação</option>
            <option value="Outro">Outro</option>
          </select>
        </div>
        <div>
          <label htmlFor="sp-participants" className={labelClass}>Número de Participantes</label>
          <select
            id="sp-participants"
            name="participants"
            value={formData.participants}
            onChange={handleChange}
            className={inputClass}
          >
            <option value="">Selecione...</option>
            <option value="Até 50">Até 50</option>
            <option value="50 a 150">50 a 150</option>
            <option value="150 a 300">150 a 300</option>
            <option value="300 a 500">300 a 500</option>
            <option value="Acima de 500">Acima de 500</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="sp-date" className={labelClass}>Data do Evento</label>
          <input
            type="date"
            id="sp-date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="sp-local" className={labelClass}>Local / Venue</label>
          <input
            type="text"
            id="sp-local"
            name="local"
            value={formData.local}
            onChange={handleChange}
            placeholder="Ex.: Expo Center Norte, Frei Caneca, WTC..."
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor="sp-message" className={labelClass}>Detalhes do Evento</label>
        <textarea
          id="sp-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          maxLength={500}
          rows={4}
          placeholder="Descreva o evento, idiomas, número de trilhas simultâneas..."
          className={`${inputClass} resize-none`}
        />
        <p className="text-xs text-gray-400 mt-1">{formData.message.length}/500</p>
      </div>

      <LgpdNotice kind="orcamento" className="mb-4 col-span-full" />
            <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-primary text-white py-4 rounded-full font-body font-bold text-base uppercase tracking-wide hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'ENVIANDO...' : 'SOLICITAR ORÇAMENTO GRATUITO'}
      </button>

      {status === 'success' && (
        <p className="text-center text-green-600 font-body text-sm font-semibold" role="status">
          Orçamento enviado! Nossa equipe retorna em até 24 horas.
        </p>
      )}
      {status === 'error' && (
        <p className="text-center text-red-500 font-body text-sm" role="alert">
          Erro ao enviar. Por favor, tente novamente ou ligue: (11) 91777-6227
        </p>
      )}
    </form>
  );
}

// ─── FAQ Item ────────────────────────────────────────────────────────────────

function FAQItem({ question, answer, index }: { question: string; answer: string; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border border-gray-100 rounded-2xl overflow-hidden"
      itemScope
      itemType="https://schema.org/Question"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-7 py-5 bg-white hover:bg-gray-50 transition-colors cursor-pointer text-left"
        aria-expanded={open}
        id={`faq-sp-${index}`}
      >
        <h4
          className="font-body font-semibold text-secondary text-base leading-snug pr-4"
          itemProp="name"
        >
          {question}
        </h4>
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-primary/10">
          <i className={`ri-${open ? 'subtract' : 'add'}-line text-primary text-base`}></i>
        </div>
      </button>
      {open && (
        <div
          className="px-7 pb-6 bg-white"
          itemScope
          itemType="https://schema.org/Answer"
          itemProp="acceptedAnswer"
        >
          <p className="font-body text-gray-600 text-sm leading-relaxed" itemProp="text">
            {answer}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Constants ───────────────────────────────────────────────────────────────

const TESTIMONIALS = depoimentosPalestra.slice(0, 3).map((d) => ({
  name: d.nome,
  text: d.texto,
  context: d.contexto,
  rating: 5,
}));

const FAQS = [
  {
    question: 'Como funciona a palestra silenciosa no Expo Center Norte e São Paulo Expo?',
    answer:
      'O sistema opera perfeitamente nesses grandes pavilhões. A transmissão por radiofrequência (RF) cobre áreas de até 200m de raio por transmissor, e utilizamos múltiplos pontos de transmissão para garantir sinal uniforme em todo o pavilhão. Já operamos no Expo Center Norte com mais de 1.200 participantes simultâneos.',
  },
  {
    question: 'Posso ter múltiplas trilhas simultâneas num mesmo espaço em SP?',
    answer:
      'Sim! Esse é exatamente o grande diferencial do sistema. É possível ter 3 trilhas de conteúdo diferentes acontecendo no mesmo salão ao mesmo tempo — cada participante escolhe no seu receptor qual canal ouvir. Perfeito para congressos, hackathons, summits e feiras de tecnologia.',
  },
  {
    question: 'A STIB atende em toda a Grande São Paulo?',
    answer:
      'Sim. Atendemos São Paulo capital (Centro, Faria Lima, Paulista, Expo Center Norte, Barra Funda, Morumbi) e toda a Grande SP: Alphaville, Barueri, Santo André, São Bernardo, Guarulhos, Campinas e região. O deslocamento é cotado individualmente conforme a distância.',
  },
  {
    question: 'O sistema funciona em espaços com bloqueio de sinal (faraday)?',
    answer:
      'Para espaços com proteção eletromagnética, utilizamos transmissores de baixa potência posicionados estrategicamente dentro do ambiente. Em casos extremos, trabalhamos com cabos de distribuição de sinal (leaky feeder). Realizamos visita técnica prévia quando necessário.',
  },
  {
    question: 'Qual o prazo mínimo para contratar palestra silenciosa em São Paulo?',
    answer:
      'Para eventos pequenos (até 100 pessoas), trabalhamos com mínimo de 3 dias úteis. Para eventos de médio e grande porte (100+ pessoas, múltiplos canais, múltiplos espaços), recomendamos 7 a 10 dias úteis para planejamento, visita técnica e logística completa.',
  },
  {
    question: 'Qual a capacidade máxima de participantes em um evento de palestra silenciosa em SP?',
    answer:
      'Nossa estrutura em São Paulo suporta eventos de até 5.000 participantes simultâneos. Já operamos no Expo Center Norte com mais de 1.200 fones ao mesmo tempo. Para eventos acima de 2.000 participantes, disponibilizamos equipe dedicada de 3 a 5 técnicos para garantir operação impecável.',
  },
];

const VENUES = [
  { name: 'Expo Center Norte', icon: 'ri-store-3-line', capacity: '15.000+' },
  { name: 'São Paulo Expo (ABCd)', icon: 'ri-building-4-line', capacity: '8.000' },
  { name: 'Centro de Conv. Rebouças', icon: 'ri-building-line', capacity: '3.000' },
  { name: 'WTC Events Center', icon: 'ri-global-line', capacity: '2.500' },
  { name: 'FIESP — Auditório', icon: 'ri-government-line', capacity: '600' },
  { name: 'Fecomercio SP', icon: 'ri-briefcase-line', capacity: '500' },
  { name: 'Centro Univ. Frei Caneca', icon: 'ri-film-line', capacity: '1.200' },
  { name: 'Grand Hyatt São Paulo', icon: 'ri-hotel-line', capacity: '800' },
];

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function SaoPauloLandingPage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: benefitsRef, isVisible: benefitsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: venuesRef, isVisible: venuesVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: testimonialsRef, isVisible: testimonialsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ threshold: 0.1 });

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

  useEffect(() => {
    document.title =
      'Palestra Silenciosa São Paulo | Congressos, Feiras e Eventos Corporativos SP | STIB';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Palestra silenciosa São Paulo: fones sem fio para congressos, feiras, summits e eventos corporativos. Expo Center Norte, WTC, Rebouças e toda a Grande SP. Orçamento em 24h!'
      );
    }

    const geoPos = document.querySelector('meta[name="geo.position"]');
    if (geoPos) geoPos.setAttribute('content', '-23.5505;-46.6333');
    const geoReg = document.querySelector('meta[name="geo.region"]');
    if (geoReg) geoReg.setAttribute('content', 'BR-SP');
    const geoPlace = document.querySelector('meta[name="geo.placename"]');
    if (geoPlace) geoPlace.setAttribute('content', 'São Paulo');

    const schemas = [
      {
        id: 'sp-localbusiness',
        data: {
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: 'STIB Palestra Silenciosa São Paulo',
          description:
            'Aluguel de fones sem fio e sistemas de palestra silenciosa para eventos corporativos, congressos, feiras e summits em São Paulo — SP.',
          url: `${siteUrl}/sao-paulo`,
          telephone: '+55-11-91777-6227',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'São Paulo',
            addressRegion: 'SP',
            addressCountry: 'BR',
          },
          geo: { '@type': 'GeoCoordinates', latitude: -23.5505, longitude: -46.6333 },
          areaServed: { '@type': 'City', name: 'São Paulo' },
        },
      },
      {
        id: 'sp-breadcrumb',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Palestra Silenciosa São Paulo',
              item: `${siteUrl}/sao-paulo`,
            },
          ],
        },
      },
      {
        id: 'sp-faq',
        data: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({
            '@type': 'Question',
            name: f.question,
            acceptedAnswer: { '@type': 'Answer', text: f.answer },
          })),
        },
      },
    ];

    schemas.forEach(({ id, data }) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const s = document.createElement('script');
      s.id = id;
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [siteUrl]);

  return (
    <>
      <Header />
      <FloatingCTA />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative min-h-screen w-full flex items-center justify-center overflow-hidden"
        aria-label="Palestra Silenciosa São Paulo — Hero"
      >
        <div className="absolute inset-0">
          <img
            src="/img/stib/cidade-sao-paulo.webp"
            alt="Palestra Silenciosa São Paulo — Eventos e congressos em SP"
            title="Palestra Silenciosa São Paulo — STIB"
            className={`w-full h-full object-cover object-top transition-all duration-1200 ${heroVisible ? 'scale-100 opacity-100' : 'scale-105 opacity-0'}`}
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/40 to-black/70"></div>
        </div>

        <div className="absolute top-24 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-6">
            <Breadcrumb
              variant="light"
              items={[
                { label: 'Home', to: '/' },
                { label: 'Cidades', to: '/cidades' },
                { label: 'Palestra Silenciosa São Paulo' },
              ]}
            />
          </div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto w-full">
          <div
            className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-semibold uppercase tracking-widest px-5 py-2 rounded-full mb-8 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-line text-primary text-sm"></i>
            </div>
            São Paulo — SP
          </div>

          <header>
            <h1
              className={`font-heading font-extrabold text-6xl md:text-8xl text-white mb-6 tracking-tight leading-none lowercase transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            >
              palestra silenciosa<br />
              <span className="text-primary">são paulo</span>
            </h1>
          </header>

          <p
            className={`font-body font-light text-xl md:text-2xl text-white/85 mb-10 max-w-3xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            Fones sem fio para congressos, feiras, summits e eventos corporativos
            em São Paulo. Multilíngue, modular e pronto para qualquer escala.
          </p>

          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <a
              href="/orcamento"
              className="inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              SOLICITAR ORÇAMENTO
              <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                <i className="ri-arrow-down-line text-white text-lg"></i>
              </div>
            </a>
            <a
              href="https://wa.me/5511999990000?text=Olá! Preciso de orçamento para palestra silenciosa em São Paulo."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-10 py-4 rounded-full font-body font-semibold text-base hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-green-400 text-xl"></i>
              </div>
              WhatsApp SP
            </a>
          </div>

          {/* Trust badges */}
          <div
            className={`flex flex-wrap items-center justify-center gap-6 mt-14 transition-all duration-700 delay-400 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            {[
              { icon: 'ri-store-3-line', label: 'Expo Center Norte e São Paulo Expo' },
              { icon: 'ri-translate-2', label: 'Múltiplos idiomas e trilhas' },
              { icon: 'ri-timer-line', label: 'Resposta em 24 horas' },
            ].map((b) => (
              <div key={b.label} className="flex items-center gap-2 text-white/80 text-sm font-body">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${b.icon} text-primary text-base`}></i>
                </div>
                {b.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ────────────────────────────────────────────── */}
      <section ref={statsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              { numericValue: 120, suffix: '+', label: 'Empresas Atendidas', icon: 'ri-briefcase-line' },
              { numericValue: 5, suffix: '', label: 'Canais Simultâneos', icon: 'ri-headphone-line' },
              { numericValue: 99, suffix: '%', label: 'Satisfação dos Clientes', icon: 'ri-star-line' },
            ].map((s, i) => (
              <AnimatedStatCard
                key={i}
                numericValue={s.numericValue}
                suffix={s.suffix}
                label={s.label}
                icon={s.icon}
                delay={i * 100}
                visible={statsVisible}
                numberColor="text-secondary"
                className="p-8 rounded-2xl bg-gradient-to-br from-primary/5 to-secondary/5"
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── FORM + INFO ──────────────────────────────────────── */}
      <section
        id="orcamento"
        ref={formRef}
        className="py-24 bg-gradient-to-br from-gray-50 to-white"
        aria-label="Formulário de orçamento palestra silenciosa São Paulo"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Info lateral */}
            <div
              className={`transition-all duration-700 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
            >
              <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
                Orçamento Gratuito · Retorno em 24 Horas
              </span>
              <h2 className="font-heading font-bold text-5xl md:text-6xl text-secondary mb-6 lowercase leading-tight">
                quanto custa palestra silenciosa em são paulo?
              </h2>
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-10">
                São Paulo concentra os maiores eventos do Brasil. Preencha o formulário ao lado
                e receba uma proposta personalizada para o seu evento no menor prazo possível.
              </p>

              <div className="space-y-6 mb-10">
                {[
                  {
                    icon: 'ri-store-3-line',
                    title: 'Expertise nas Feiras e Pavilhões de SP',
                    desc: 'Já operamos no Expo Center Norte, São Paulo Expo, Rebouças e WTC. Sabemos exatamente como dimensionar o sistema para cada espaço e evitar pontos cegos de sinal.',
                  },
                  {
                    icon: 'ri-stack-line',
                    title: 'Múltiplas Trilhas Simultâneas',
                    desc: 'Até 5 canais independentes no mesmo espaço. Ideal para hackathons, summits com breakouts, feiras com múltiplos estandes e congressos científicos com sessões paralelas.',
                  },
                  {
                    icon: 'ri-translate-2',
                    title: 'Tradução e Conteúdo Multilíngue',
                    desc: 'Sistema compatível com intérpretes de português, inglês, espanhol, mandarim e mais. Perfeito para eventos internacionais, feiras B2B e encontros com parceiros estrangeiros.',
                  },
                  {
                    icon: 'ri-tools-line',
                    title: 'Técnico Dedicado do Início ao Fim',
                    desc: 'Chegamos antes para setup, ficamos durante o evento e garantimos suporte em tempo real. Para eventos de grande porte, disponibilizamos equipe de 2 ou mais técnicos.',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-5">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                      <i className={`${item.icon} text-primary text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-secondary text-base mb-1">{item.title}</h3>
                      <p className="font-body text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-secondary/5 border border-secondary/10 rounded-2xl p-6">
                <p className="font-body text-sm text-gray-600 leading-relaxed">
                  <strong className="text-secondary">Atendimento em São Paulo — 2026:</strong><br />
                  Até 5 canais simultâneos no mesmo espaço<br />
                  Cobertura total na capital e Grande SP<br />
                  Equipe técnica dedicada do setup ao encerramento
                </p>
              </div>
            </div>

            {/* Formulário */}
            <div
              className={`bg-white rounded-3xl p-10 border border-gray-100 transition-all duration-700 delay-200 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary">
                  <i className="ri-send-plane-line text-white text-lg"></i>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-secondary">Solicite seu Orçamento</h3>
                  <p className="font-body text-xs text-gray-400">Gratuito · Sem compromisso · Resposta em 24h</p>
                </div>
              </div>
              <QuoteForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── DIFERENCIAIS SP ──────────────────────────────────── */}
      <section ref={benefitsRef} className="py-24 bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Por que escolher a STIB em SP
            </span>
            <h2
              className={`font-heading font-bold text-5xl md:text-6xl lowercase transition-all duration-700 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              feito para o maior mercado de eventos do brasil
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-store-3-line',
                title: 'Líderes em Feiras e Exposições',
                desc: 'SP concentra as maiores feiras do país — Expo Center Norte, São Paulo Expo, ABCd. Nossa estrutura está dimensionada para eventos com 500 a 10.000+ participantes.',
              },
              {
                icon: 'ri-code-box-line',
                title: 'Summits de Tecnologia e Inovação',
                desc: 'O ecossistema de startups e tech de SP exige soluções inovadoras. Nosso sistema cobre hackathons, pitch days, campus events e eventos da comunidade tech com facilidade.',
              },
              {
                icon: 'ri-stack-line',
                title: 'Sessões Paralelas sem Limite',
                desc: 'Até 5 canais de conteúdo rodando simultaneamente no mesmo espaço físico. Sem divisórias, sem barreiras acústicas — só fone e foco no conteúdo certo.',
              },
              {
                icon: 'ri-map-2-line',
                title: 'Grande SP Toda Coberta',
                desc: 'São Paulo capital, Alphaville, Barueri, Guarulhos, ABC Paulista e Campinas. Nossa logística alcança qualquer ponto da Grande SP para o seu evento.',
              },
              {
                icon: 'ri-global-line',
                title: 'Eventos Internacionais e B2B',
                desc: 'SP é sede de eventos com delegações de 40+ países. Suportamos até 5 idiomas simultâneos e trabalhamos com intérpretes de qualquer nacionalidade.',
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Equipe Técnica Completa',
                desc: 'Para grandes eventos em SP, disponibilizamos equipes de 2 a 5 técnicos. Coordenação, setup, manutenção e desmontagem — tudo incluso e profissional.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className={`bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary/20 mb-6">
                  <i className={`${item.icon} text-primary text-2xl`}></i>
                </div>
                <h3 className="font-heading font-bold text-white text-lg mb-3">{item.title}</h3>
                <p className="font-body text-white/70 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VENUES ──────────────────────────────────────────── */}
      <section ref={venuesRef} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div
              className={`transition-all duration-700 ${venuesVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
            >
              <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
                Onde já atuamos
              </span>
              <h2 className="font-heading font-bold text-5xl md:text-6xl text-secondary mb-6 lowercase leading-tight">
                principais venues em são paulo
              </h2>
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-10">
                Do Expo Center Norte ao Grand Hyatt, a <strong>palestra silenciosa em São Paulo</strong> já
                passou pelos maiores e mais exigentes venues da cidade. Veja onde já operamos:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {VENUES.map((v, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-4 p-4 rounded-xl bg-gray-50 transition-all duration-500 ${venuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                    style={{ transitionDelay: `${i * 60}ms` }}
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                      <i className={`${v.icon} text-primary text-base`}></i>
                    </div>
                    <div>
                      <p className="font-body font-semibold text-secondary text-sm">{v.name}</p>
                      <p className="font-body text-xs text-gray-400">até {v.capacity} pessoas</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              className={`relative h-[520px] rounded-3xl overflow-hidden transition-all duration-700 delay-200 ${venuesVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
            >
              <img
                src="/img/stib/cidade-sao-paulo.webp"
                alt="Centro de Convenções em São Paulo — Palestra Silenciosa SP"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <p className="font-body text-white/80 text-sm mb-2">Atendimento completo em</p>
                <h3 className="font-heading font-bold text-white text-2xl">São Paulo e Grande SP</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── DEPOIMENTOS ──────────────────────────────────────── */}
      <section
        ref={testimonialsRef}
        className="py-24 bg-gradient-to-br from-gray-50 to-white"
        aria-label="Depoimentos de clientes São Paulo"
        itemScope
        itemType="https://schema.org/ItemList"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Quem já contratou em SP
            </span>
            <h2
              className={`font-heading font-bold text-5xl md:text-6xl text-secondary lowercase transition-all duration-700 ${testimonialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              o que dizem nossos clientes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl p-8 border border-gray-100 transition-all duration-700 ${testimonialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <div className="flex items-center gap-1 mb-5">
                  {[...Array(t.rating)].map((_, j) => (
                    <i key={j} className="ri-star-fill text-yellow-400 text-base"></i>
                  ))}
                </div>
                <p
                  className="font-body text-gray-600 text-sm leading-relaxed mb-6 italic"
                 
                >
                  "{t.text}"
                </p>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary/10 flex-shrink-0">
                    <span className="font-heading font-bold text-secondary text-lg">
                      {t.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-heading font-bold text-secondary text-sm">
                      {t.name}
                    </p>
                    {t.context && (
                      <p className="font-body text-xs text-gray-500">{t.context}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────── */}
      <section
        ref={faqRef}
        className="py-24 bg-white"
        aria-label="Perguntas frequentes palestra silenciosa São Paulo"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Dúvidas Frequentes
            </span>
            <h2
              className={`font-heading font-bold text-5xl md:text-6xl text-secondary lowercase transition-all duration-700 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              faq — são paulo
            </h2>
          </div>

          <div
            className={`space-y-4 transition-all duration-700 delay-200 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {FAQS.map((f, i) => (
              <FAQItem key={i} question={f.question} answer={f.answer} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────── */}
      <section className="relative py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/img/stib/setor-tecnologia.webp"
            alt="Solicite orçamento palestra silenciosa São Paulo"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary/85 via-secondary/70 to-secondary/50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            <div className="max-w-2xl">
              <h2 className="font-heading font-bold text-5xl md:text-6xl text-white lowercase leading-tight mb-6">
                pronto para transformar<br />
                seu evento em são paulo?
              </h2>
              <p className="font-body text-lg text-white/80 leading-relaxed">
                <strong className="text-white">Agenda com vagas limitadas</strong> para eventos em São Paulo.
                Confirme sua data e receba orçamento completo em até 24 horas.
              </p>
            </div>

            <div className="flex flex-col gap-4 flex-shrink-0">
              <a
                href="/orcamento"
                className="inline-flex items-center justify-center gap-3 bg-primary text-white px-12 py-5 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                VER FORMULÁRIO DE ORÇAMENTO
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-arrow-up-line text-white text-lg"></i>
                </div>
              </a>
              <a
                href="https://wa.me/5511999990000?text=Olá! Preciso de orçamento para palestra silenciosa em São Paulo."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-white text-secondary px-12 py-5 rounded-full font-body font-bold text-base uppercase hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-green-500 text-xl"></i>
                </div>
                FALAR NO WHATSAPP
              </a>
              <Link
                to="/blog/palestra-silenciosa-sao-paulo-guia-completo"
                className="text-center font-body text-sm text-white/70 hover:text-white underline underline-offset-4 transition-colors cursor-pointer"
              >
                Ler nosso guia completo sobre Palestra Silenciosa São Paulo &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>

      <NossosFones />
      <PacotesBox texto="Em São Paulo capital, empresas, academias, escolas e organizadores com agenda recorrente podem contratar pacotes mensais ou pacotes de eventos: fones e equipe reservados nas suas datas, com valor fechado e previsível, em vez de contratar evento a evento." />
      <Footer />
    </>
  );
}